import {
  useCallback,
  useId,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { cx } from '@/components/ui';
import { useDialogBehavior } from '@/components/ui/internal/useDialogBehavior';
import {
  AppShellContext,
  DESKTOP_MEDIA_QUERY,
  SIDENAV_COLLAPSED_STORAGE_KEY,
  type AppShellContextValue,
} from './appShellContext';
import styles from './AppShell.module.css';

export interface AppShellProps {
  /** Usually a <SideNav />. */
  sideNav: ReactNode;
  /** Usually a <TopBar />. */
  topBar: ReactNode;
  children: ReactNode;
  /** Footer pinned to the bottom of the viewport below the content (e.g. copyright). */
  footer?: ReactNode;
  skipLinkLabel?: string;
  /** id of the <main> element (skip-link target). */
  mainId?: string;
  /** Accessible name of the navigation drawer on small screens. */
  navDrawerLabel?: string;
  className?: string;
  mainClassName?: string;
}

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(SIDENAV_COLLAPSED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeCollapsed(value: boolean): void {
  try {
    window.localStorage.setItem(SIDENAV_COLLAPSED_STORAGE_KEY, String(value));
  } catch {
    // Storage unavailable; the state still applies for this session.
  }
}

function subscribeDesktop(onChange: () => void): () => void {
  if (typeof window.matchMedia !== 'function') return () => undefined;
  const mql = window.matchMedia(DESKTOP_MEDIA_QUERY);
  mql.addEventListener?.('change', onChange);
  return () => mql.removeEventListener?.('change', onChange);
}

function getDesktop(): boolean {
  if (typeof window.matchMedia !== 'function') return true;
  return window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
}

function getServerDesktop(): boolean {
  return true;
}

/**
 * Application frame: skip link, side navigation, sticky top bar and the main landmark.
 * Below 1024px the navigation becomes a modal drawer; on desktop it can collapse to an icon rail.
 */
export function AppShell({
  sideNav,
  topBar,
  children,
  footer,
  skipLinkLabel = 'Skip to main content',
  mainId = 'main-content',
  navDrawerLabel = 'Navigation',
  className,
  mainClassName,
}: AppShellProps) {
  const navId = useId();
  const isDesktop = useSyncExternalStore(subscribeDesktop, getDesktop, getServerDesktop);
  const [collapsed, setCollapsedState] = useState(readCollapsed);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);
  const drawerOpen = !isDesktop && mobileNavOpen;

  const setCollapsed = useCallback((value: boolean) => {
    writeCollapsed(value);
    setCollapsedState(value);
  }, []);

  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);
  const openMobileNav = useCallback(() => setMobileNavOpen(true), []);

  useDialogBehavior({
    open: drawerOpen,
    containerRef: navRef,
    onEscape: closeMobileNav,
    lockScroll: true,
  });

  const value = useMemo<AppShellContextValue>(
    () => ({
      collapsed: isDesktop && collapsed,
      setCollapsed,
      toggleCollapsed: () => setCollapsed(!collapsed),
      isDesktop,
      mobileNavOpen: drawerOpen,
      openMobileNav,
      closeMobileNav,
      toggleNav: () => {
        if (isDesktop) setCollapsed(!collapsed);
        else setMobileNavOpen((open) => !open);
      },
      navId,
    }),
    [isDesktop, collapsed, setCollapsed, drawerOpen, openMobileNav, closeMobileNav, navId],
  );

  return (
    <AppShellContext.Provider value={value}>
      <div
        className={cx(
          styles.shell,
          isDesktop && collapsed && styles.collapsed,
          drawerOpen && styles.drawerOpen,
          className,
        )}
      >
        <a
          className={styles.skipLink}
          href={`#${mainId}`}
          onClick={(event) => {
            const main = document.getElementById(mainId);
            if (!main) return;
            event.preventDefault();
            main.focus();
            main.scrollIntoView?.({ block: 'start' });
          }}
        >
          {skipLinkLabel}
        </a>
        {drawerOpen && (
          <div className={styles.overlay} aria-hidden="true" onClick={closeMobileNav} />
        )}
        <div
          ref={navRef}
          id={navId}
          className={styles.navArea}
          inert={!isDesktop && !mobileNavOpen}
          {...(drawerOpen
            ? { role: 'dialog', 'aria-modal': true, 'aria-label': navDrawerLabel }
            : {})}
        >
          {sideNav}
        </div>
        <div className={styles.content}>
          <div className={styles.topBar}>{topBar}</div>
          <main id={mainId} tabIndex={-1} className={cx(styles.main, mainClassName)}>
            {children}
          </main>
          {footer && <footer className={styles.footer}>{footer}</footer>}
        </div>
      </div>
    </AppShellContext.Provider>
  );
}
